import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Country, State, City } from "country-state-city";
import {
  Search,
  Building2,
  Tag as TagIcon,
  X as XIcon,
  Check,
  ChevronDown,
  ChevronUp,
  Plus,
  Sparkles,
  Calendar as CalendarIcon,
} from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import useDefaultCountryCode from "../../../components/hook/use-default-country-code";
import ReuseableDrawer from "../../../components/resuable-drawer";
import Pagination from "../../../components/pagination";
import {
  BusinessRecord,
  formatBusinessDate,
  formatBusinessNumber,
  getBusinessStatusStyle,
} from "./types";

const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20 focus:border-primary-400";

// ─── Country / dial-code data — same "duplicate locally" convention used by
// create-reseller-modal.tsx and update-customer.tsx rather than a shared
// module. ─────────────────────────────────────────────────────────────────
const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countryCodeOptions = Country.getAllCountries()
  .map((country) => ({
    value: normalizeDialCode(country.phonecode),
    label: `${normalizeDialCode(country.phonecode)} ${country.isoCode}`,
    isoCode: country.isoCode,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countryCodeOptions.find((c) => c.value === normalizeDialCode(dialCode));

const countries = Country.getAllCountries().map((c) => ({ value: c.name, label: c.name, isoCode: c.isoCode }));
const getCountryByName = (name?: string) => countries.find((c) => c.value.toLowerCase() === (name || "").toLowerCase());

// Splits a stored phone string ("+91-9788882888", or a bare "+2348012345678")
// into { countryCode, local } for the combined edit-mode field, and the
// reverse join on save.
const splitStoredPhone = (phone?: string | null, fallbackDialCode = "+234") => {
  const raw = (phone || "").trim();
  if (!raw) return { countryCode: fallbackDialCode, local: "" };
  const withoutDash = raw.replace(/^(\+\d+)-/, "$1");
  const sorted = [...countryCodeOptions].sort((a, b) => b.value.length - a.value.length);
  const match = sorted.find((c) => withoutDash.startsWith(c.value));
  if (match) return { countryCode: match.value, local: withoutDash.slice(match.value.length) };
  return { countryCode: fallbackDialCode, local: raw.replace(/^\+/, "") };
};

// Deterministic color per tag, so a business's tags read as distinct chips
// (matching the design) rather than one flat color for every tag.
const TAG_COLOR_STYLES = [
  "bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-400",
  "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
];
const getTagColorStyle = (tag: string): string => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) >>> 0;
  return TAG_COLOR_STYLES[hash % TAG_COLOR_STYLES.length];
};

// Country ISO -> "K.A."-style abbreviation for the Location row.
const formatStateAbbrev = (iso?: string) => (iso ? `${iso.split("").join(".")}.` : undefined);

// Flag + dial code dropdown — same pattern as create-reseller-modal.tsx.
const CountryCodeSelect: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = getCountryByDialCode(value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-24 shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-center gap-1 px-2 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
      >
        {selected && (
          <img
            src={`https://flagcdn.com/24x18/${selected.isoCode.toLowerCase()}.png`}
            alt=""
            className="h-3.5 w-5 rounded-sm object-cover shrink-0"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <span className="truncate">{value || "+234"}</span>
        <ChevronDown size={12} className={`ml-auto shrink-0 text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {countryCodeOptions.map((option) => (
            <button
              key={option.isoCode}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                option.value === value ? "bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
              }`}
            >
              <img
                src={`https://flagcdn.com/24x18/${option.isoCode.toLowerCase()}.png`}
                alt=""
                className="h-3.5 w-5 rounded-sm object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Plain native select with a decorative chevron — Country/State/City/Status.
const SelectField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder = "Select", disabled }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full appearance-none px-3 py-2.5 pr-8 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20 disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
  </div>
);

// One label-left/value-right row — the "Business Info"/"Facility Details"
// list style. `link` renders the value as an "Add X" affordance instead
// (empty field), `highlight` gives it the light pill treatment (dates).
const InfoRow: React.FC<{
  label: string;
  value?: React.ReactNode;
  highlight?: boolean;
  link?: { label: string; onClick: () => void };
}> = ({ label, value, highlight, link }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className="text-[14px] text-[#91989F] font-medium dark:text-gray-400 shrink-0">{label}</span>
    {link ? (
      <button
        type="button"
        onClick={link.onClick}
        className="text-[14px] font-semibold text-[#4338CA] dark:text-primary-400 underline hover:text-primary-700 dark:hover:text-primary-300 shrink-0"
      >
        {link.label}
      </button>
    ) : highlight ? (
      <span className="text-[14px] font-semibold text-[#1D4ED8] dark:text-blue-400 bg-[#E8F1FF] dark:bg-blue-500/15 px-2.5 py-1 rounded-md shrink-0">
        {value ?? "—"}
      </span>
    ) : (
      <span className="text-[14px] font-medium text-[#3A3428] dark:text-gray-100 text-right break-words">{value ?? "—"}</span>
    )}
  </div>
);

interface BusinessEditForm {
  name: string;
  status: string;
  stage: string;
  email: string;
  country_code: string;
  phone: string; // local number only — country_code is combined back in on save
  website: string;
  country: string;
  state: string;
  city: string;
  estimated_value: string;
  tags: string[];
  org_code: string;
  total_residents: string;
  total_units: string;
  occupied_units: string;
  vacant_units: string;
  total_facility_admins: string;
  org_onboarded_date: string;
}

const toEditForm = (business: BusinessRecord, fallbackDialCode = "+234"): BusinessEditForm => {
  const custom = business.custom || {};
  const toStr = (n?: number | null) => (n === null || n === undefined ? "" : String(n));
  const toDateInput = (v?: string | null) => {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  };
  const { countryCode, local } = splitStoredPhone(business.phone, fallbackDialCode);
  return {
    name: business.name || "",
    status: business.status || "active",
    stage: business.stage || "",
    email: business.email || "",
    country_code: countryCode,
    phone: local,
    website: business.website || "",
    country: (custom.country as string) || "",
    state: (custom.state as string) || "",
    city: (custom.city as string) || "",
    estimated_value: toStr(business.estimated_value),
    tags: business.tags || [],
    org_code: (custom.org_code as string) || "",
    total_residents: toStr(custom.total_residents as number | null),
    total_units: toStr(custom.total_units as number | null),
    occupied_units: toStr(custom.occupied_units as number | null),
    vacant_units: toStr(custom.vacant_units as number | null),
    total_facility_admins: toStr(custom.total_facility_admins as number | null),
    org_onboarded_date: toDateInput(custom.org_onboarded_date as string | null),
  };
};

const BusinessDetailsDrawer: React.FC<{
  business: BusinessRecord | null;
  onClose: () => void;
  onUpdated: (updated: BusinessRecord) => void;
}> = ({ business, onClose, onUpdated }) => {
  const token = localStorage.getItem("authToken");
  const custom = business?.custom || {};
  // Falls back to the signed-in user's own country when a business has no
  // phone on file yet, instead of hardcoding Nigeria for everyone.
  const defaultCountryCode = useDefaultCountryCode();

  const [isEditing, setIsEditing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<BusinessEditForm | null>(null);
  const [tagInput, setTagInput] = useState("");

  // Notes have their own always-available mini save flow, independent of
  // the "Edit Details"/"Save Details" toggle for the rest of the fields.
  const [noteDraft, setNoteDraft] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(true);

  const { makeRequest: updateBusiness } = useCrmApi(`/businesses/${business?.id || ""}`, "PATCH", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    setIsEditing(false);
    setIsFullscreen(false);
    setForm(business ? toEditForm(business, defaultCountryCode) : null);
    setTagInput("");
    setNoteDraft(business?.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const startEditing = () => {
    if (business) setForm(toEditForm(business, defaultCountryCode));
    setIsEditing(true);
    setIsFullscreen(true);
  };

  const cancelEditing = () => {
    if (business) setForm(toEditForm(business, defaultCountryCode));
    setIsEditing(false);
    setIsFullscreen(false);
    setTagInput("");
  };

  const updateField = <K extends keyof BusinessEditForm>(key: K, value: BusinessEditForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const addTag = () => {
    const value = tagInput.trim();
    if (!value || !form) return;
    if (!form.tags.includes(value)) updateField("tags", [...form.tags, value]);
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    if (!form) return;
    updateField("tags", form.tags.filter((t) => t !== tag));
  };

  const handleSave = async () => {
    if (!business || !form) return;
    setIsSaving(true);
    const toNum = (v: string) => (v.trim() === "" ? null : Number(v));
    const combinedPhone = form.phone.trim() ? `${form.country_code}-${form.phone.trim()}` : null;
    const payload = {
      name: form.name,
      status: form.status,
      stage: form.stage || null,
      email: form.email || null,
      phone: combinedPhone,
      website: form.website || null,
      estimated_value: toNum(form.estimated_value),
      tags: form.tags,
      custom: {
        ...custom,
        country: form.country || null,
        state: form.state || null,
        city: form.city || null,
        org_code: form.org_code || null,
        total_residents: toNum(form.total_residents),
        total_units: toNum(form.total_units),
        occupied_units: toNum(form.occupied_units),
        vacant_units: toNum(form.vacant_units),
        total_facility_admins: toNum(form.total_facility_admins),
        org_onboarded_date: form.org_onboarded_date || null,
      },
    };

    try {
      const [response, status] = await updateBusiness(payload);
      if (status >= 200 && status < 300) {
        toast.success("Business updated.");
        onUpdated({ ...business, ...payload } as BusinessRecord);
        setIsEditing(false);
        setIsFullscreen(false);
      } else {
        toast.error(response?.message || "Failed to update business.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNote = async () => {
    if (!business) return;
    setIsSavingNote(true);
    const [response, status] = await updateBusiness({ notes: noteDraft || null });
    setIsSavingNote(false);
    if (status >= 200 && status < 300) {
      toast.success("Note saved.");
      onUpdated({ ...business, notes: noteDraft || null } as BusinessRecord);
    } else {
      toast.error(response?.message || "Failed to save note.");
    }
  };

  const handleDeleteNote = async () => {
    if (!business) return;
    setIsSavingNote(true);
    const [response, status] = await updateBusiness({ notes: null });
    setIsSavingNote(false);
    if (status >= 200 && status < 300) {
      toast.success("Note deleted.");
      setNoteDraft("");
      onUpdated({ ...business, notes: null } as BusinessRecord);
    } else {
      toast.error(response?.message || "Failed to delete note.");
    }
  };

  // Country -> State -> City cascade, derived live from the edit form's
  // own country/state (country-state-city's lookups are synchronous, so a
  // plain derivation is enough — no effect/fetch needed).
  const selectedCountry = form ? getCountryByName(form.country) : undefined;
  const stateOptions = selectedCountry
    ? State.getStatesOfCountry(selectedCountry.isoCode).map((s) => ({ value: s.name, label: s.name, isoCode: s.isoCode }))
    : [];
  const selectedStateObj = form ? stateOptions.find((s) => s.value === form.state) : undefined;
  const cityOptions =
    selectedCountry && selectedStateObj
      ? City.getCitiesOfState(selectedCountry.isoCode, selectedStateObj.isoCode).map((c) => ({ value: c.name, label: c.name }))
      : [];

  // Location row (view mode) — prefer the split country/state/city fields;
  // fall back to the legacy single address string for older records.
  const viewCountry = getCountryByName(custom.country as string);
  const viewStateIso = viewCountry
    ? State.getStatesOfCountry(viewCountry.isoCode).find((s) => s.name === custom.state)?.isoCode
    : undefined;
  const locationValue = custom.city
    ? [custom.city, formatStateAbbrev(viewStateIso) || custom.state].filter(Boolean).join(", ")
    : business?.address || (custom.org_address as string) || undefined;

  return (
    <ReuseableDrawer
      isOpen={!!business}
      onClose={onClose}
      title="Business Overview"
      titleIcon={<Building2 size={18} className="text-gray-900 dark:text-gray-100" />}
      initialWidth="38%"
      fullscreen={isFullscreen}
      onFullscreenChange={setIsFullscreen}
    >
      {business && form && (
        <div className="p-5 sm:p-6">
          <div className="max-w-3xl mx-auto space-y-5">
            {isEditing && (
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">Edit business details</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Add or edit existing details of the customer</p>
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-11 h-11 rounded-full bg-primary-400 dark:bg-primary-500 flex items-center justify-center shrink-0">
                  <Building2 size={18} className="text-white" />
                </div>
                {isEditing ? (
                  <input
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    className={`${inputClass} font-bold text-base`}
                    placeholder="Business name"
                  />
                ) : (
                  <p className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">{business.name}</p>
                )}
              </div>
              {!isEditing && (
                <button
                  type="button"
                  onClick={startEditing}
                  className="text-sm font-semibold text-primary-600 dark:text-primary-400 underline hover:text-primary-700 dark:hover:text-primary-300 shrink-0"
                >
                  Edit Details
                </button>
              )}
            </div>

            {isEditing && (
              <div className="flex items-center gap-1.5 -mt-3 ml-14">
                <select
                  value={form.status}
                  onChange={(e) => updateField("status", e.target.value)}
                  className="text-[11px] font-medium px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20"
                >
                  <option value="active">active</option>
                  <option value="disabled">disabled</option>
                </select>
                <input
                  value={form.stage}
                  onChange={(e) => updateField("stage", e.target.value)}
                  placeholder="Stage"
                  className="text-[11px] font-medium px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 w-24 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20"
                />
              </div>
            )}

            {/* Business Info */}
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Business Info</p>
              {isEditing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Phone/ WhatsApp</label>
                      <div className="flex gap-2">
                        <CountryCodeSelect value={form.country_code} onChange={(v) => updateField("country_code", v)} />
                        <input
                          value={form.phone}
                          onChange={(e) => updateField("phone", e.target.value.replace(/\D/g, ""))}
                          placeholder="Phone number"
                          className={`${inputClass} flex-1`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Email</label>
                      <input value={form.email} onChange={(e) => updateField("email", e.target.value)} className={inputClass} placeholder="Email" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Country</label>
                      <SelectField
                        value={form.country}
                        onChange={(v) => setForm((prev) => (prev ? { ...prev, country: v, state: "", city: "" } : prev))}
                        options={countries}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">State</label>
                      <SelectField
                        value={form.state}
                        onChange={(v) => setForm((prev) => (prev ? { ...prev, state: v, city: "" } : prev))}
                        options={stateOptions}
                        disabled={!form.country}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">City</label>
                      <SelectField value={form.city} onChange={(v) => updateField("city", v)} options={cityOptions} disabled={!form.state} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Website</label>
                    <input
                      value={form.website}
                      onChange={(e) => updateField("website", e.target.value)}
                      placeholder="e.g. acme.com"
                      className={inputClass}
                    />
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800/60">
                  <InfoRow label="Phone Number:" value={business.phone || undefined} />
                  <InfoRow label="Email" value={business.email || undefined} />
                  <InfoRow label="Location" value={locationValue} />
                  <InfoRow
                    label="Website"
                    value={business.website || undefined}
                    link={!business.website ? { label: "Add Website", onClick: startEditing } : undefined}
                  />
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* Facility Details */}
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Facility Details</p>
              {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Org Code</label>
                    <input value={form.org_code} onChange={(e) => updateField("org_code", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Onboarded</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={form.org_onboarded_date}
                        onChange={(e) => updateField("org_onboarded_date", e.target.value)}
                        className={`${inputClass} pr-9`}
                      />
                      <CalendarIcon size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Source System</label>
                    <div className="px-3 py-2.5 text-sm rounded-lg bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/20 text-gray-700 dark:text-gray-300 truncate">
                      {custom.source_system || "—"}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Total Residents</label>
                    <input type="number" value={form.total_residents} onChange={(e) => updateField("total_residents", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Total Units</label>
                    <input type="number" value={form.total_units} onChange={(e) => updateField("total_units", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Estimated Value</label>
                    <input type="number" value={form.estimated_value} onChange={(e) => updateField("estimated_value", e.target.value)} className={inputClass} />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Occupied Units</label>
                    <input type="number" value={form.occupied_units} onChange={(e) => updateField("occupied_units", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Vacant Units</label>
                    <input type="number" value={form.vacant_units} onChange={(e) => updateField("vacant_units", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Facility Admins</label>
                    <input
                      type="number"
                      value={form.total_facility_admins}
                      onChange={(e) => updateField("total_facility_admins", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800/60">
                  <InfoRow label="Organisation Code" value={custom.org_code} />
                  <InfoRow label="Onboarded On" value={formatBusinessDate(custom.org_onboarded_date)} highlight />
                  <InfoRow label="Source System" value={custom.source_system} />
                  <InfoRow label="Total Residents" value={formatBusinessNumber(custom.total_residents)} />
                  <InfoRow
                    label="Total Units"
                    value={custom.total_units != null ? formatBusinessNumber(custom.total_units) : undefined}
                    link={custom.total_units == null ? { label: "Add Units", onClick: startEditing } : undefined}
                  />
                  <InfoRow label="Occupied Units" value={formatBusinessNumber(custom.occupied_units)} />
                  <InfoRow label="Vacant Units" value={formatBusinessNumber(custom.vacant_units)} />
                  <InfoRow label="Estimated Value" value={formatBusinessNumber(business.estimated_value)} />
                  <InfoRow label="Facility Admins" value={formatBusinessNumber(custom.total_facility_admins)} />
                  <InfoRow label="Last Synced On" value={formatBusinessDate(custom.last_synced_at)} highlight />
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* Business Tags */}
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Business Tags</p>
              <div className={isEditing ? "flex flex-wrap gap-1.5 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700" : "flex flex-wrap items-center gap-1.5"}>
                {form.tags.length === 0 && <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No tags yet.</p>}
                {form.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md ${getTagColorStyle(tag)}`}
                  >
                    {tag}
                    {isEditing && (
                      <button type="button" onClick={() => removeTag(tag)} className="hover:opacity-70">
                        <XIcon size={10} />
                      </button>
                    )}
                  </span>
                ))}
                {!isEditing && (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Add New <Plus size={11} />
                  </button>
                )}
              </div>
              {isEditing && (
                <div className="flex items-center gap-2 mt-2.5">
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Add a tag and press Enter"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-3 py-2.5 text-xs font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/15 hover:bg-primary-100 dark:hover:bg-primary-500/25 rounded-lg shrink-0"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* Notes — independent of the Edit Details toggle */}
            <div>
              <button
                type="button"
                onClick={() => !isFullscreen && setNotesExpanded((s) => !s)}
                className="w-full flex items-center justify-between mb-2"
              >
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">Add Notes</span>
                {!isFullscreen && (notesExpanded ? <ChevronUp size={16} className="text-[#7B7B7B] font-semibold dark:text-gray-500" /> : <ChevronDown size={16} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />)}
              </button>

              {(isFullscreen || notesExpanded) && (
                <div>
                  {business.notes && (
                    <div className="rounded-lg bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/20 p-3 mb-2.5">
                      <p className="text-sm text-gray-800 dark:text-gray-100 mb-1.5 break-words">{business.notes}</p>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setNoteDraft(business.notes || "")}
                          className="text-xs font-semibold text-primary-600 dark:text-primary-400 underline hover:text-primary-700"
                        >
                          Edit Note
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteNote}
                          disabled={isSavingNote}
                          className="text-xs font-semibold text-primary-600 dark:text-primary-400 underline hover:text-primary-700 disabled:opacity-50"
                        >
                          Delete Note
                        </button>
                      </div>
                    </div>
                  )}

                  {isFullscreen ? (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Business notes</label>
                        <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">{noteDraft.length}/300</span>
                      </div>
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value.slice(0, 300))}
                        rows={4}
                        placeholder="Your note"
                        className={`${inputClass} resize-none`}
                      />
                      <div className="flex items-center justify-between mt-2">
                        <button
                          type="button"
                          onClick={() => toast("AI note suggestions are coming soon.")}
                          className="flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700"
                        >
                          <Sparkles size={14} /> <span className="underline">Suggest</span>
                        </button>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setNoteDraft(business.notes || "")}
                            className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            Discard
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveNote}
                            disabled={isSavingNote}
                            className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 rounded-lg transition-colors"
                          >
                            {isSavingNote ? "Saving…" : "Save Note"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">Add notes for your reference</p>
                      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        <input
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          placeholder="New Note"
                          className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none"
                        />
                        <button
                          type="button"
                          onClick={handleSaveNote}
                          disabled={isSavingNote || !noteDraft.trim()}
                          className="shrink-0 w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="flex items-center justify-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={cancelEditing}
                  disabled={isSaving}
                  className="flex-1 max-w-[220px] px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 max-w-[220px] flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 rounded-lg shadow-sm transition-colors"
                >
                  {isSaving ? "Saving…" : (<><Check size={14} /> Save Details</>)}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </ReuseableDrawer>
  );
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

const Businesses: React.FC = () => {
  const token = localStorage.getItem("authToken");

  const [businesses, setBusinesses] = useState<BusinessRecord[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessRecord | null>(null);

  const { makeRequest: getBusinesses } = useCrmApi(`/businesses`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  // Debounce search before sending it as a query param.
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Jump back to page 1 whenever the search or filter changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const fetchBusinesses = async (showPaginationLoader = false) => {
    if (showPaginationLoader) setIsPaginationLoading(true);
    else setInitialLoading(true);
    try {
      const params: Record<string, unknown> = { page: currentPage, per_page: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;

      const [response] = await getBusinesses(undefined, params);
      const results: BusinessRecord[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
          ? response.results
          : Array.isArray(response?.data)
            ? response.data
            : [];

      setBusinesses(results);
      const totalCount = response?.total ?? results.length;
      const perPage = response?.per_page ?? pageSize;
      setTotal(totalCount);
      setTotalPages(Math.max(1, Math.ceil(totalCount / perPage)));
    } catch (error) {
      console.error("Error fetching businesses:", error);
      setBusinesses([]);
      setTotalPages(1);
    } finally {
      setInitialLoading(false);
      setIsPaginationLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialLoading) fetchBusinesses(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearch, statusFilter]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Businesses</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {total > 0 ? `${total.toLocaleString()} businesses` : "Facilities and organizations synced to your CRM."}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search businesses by name"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20 focus:border-primary-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20"
            >
              {STATUS_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Business</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Source</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Residents</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Admins</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Tags</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Onboarded</th>
                </tr>
              </thead>
              <tbody>
                {initialLoading || isPaginationLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                        <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</span>
                      </div>
                    </td>
                  </tr>
                ) : businesses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                      {debouncedSearch || statusFilter ? "No businesses match your search." : "No businesses found yet."}
                    </td>
                  </tr>
                ) : (
                  businesses.map((business) => (
                    <tr
                      key={business.id}
                      onClick={() => setSelectedBusiness(business)}
                      className="border-b border-gray-50 dark:border-gray-800 hover:bg-primary-50/30 dark:hover:bg-primary-500/10 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-500/15 flex items-center justify-center shrink-0">
                            <Building2 size={16} className="text-primary-500 dark:text-primary-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{business.name}</p>
                            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">
                              {business.custom?.org_code || business.external_business_id || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300">
                        {business.custom?.source_system || "—"}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300">
                        {formatBusinessNumber(business.custom?.total_residents)}
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300">
                        {formatBusinessNumber(business.custom?.total_facility_admins)}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex flex-wrap gap-1">
                          {(business.tags || []).slice(0, 2).map((tag) => (
                            <span key={tag} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${getTagColorStyle(tag)}`}>
                              <TagIcon size={9} /> {tag}
                            </span>
                          ))}
                          {(business.tags || []).length === 0 && <span className="text-xs text-gray-300 dark:text-gray-600">—</span>}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getBusinessStatusStyle(business.status)}`}>
                          {business.status || "unknown"}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300">
                        {formatBusinessDate(business.custom?.org_onboarded_date)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pt-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              isLoading={isPaginationLoading}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <BusinessDetailsDrawer
        business={selectedBusiness}
        onClose={() => setSelectedBusiness(null)}
        onUpdated={(updated) => {
          setSelectedBusiness(updated);
          setBusinesses((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
        }}
      />
    </div>
  );
};

export default Businesses;
